import {
  formatCountries,
  formatPhonePrefixes,
  setValidationRules,
} from '../../shared/helpers/helpers';
import {actions, ActionTypes, XeroSignupAction} from '../actions';
import {XeroSignupState} from '../state';
import {
  Country,
  KeyValue,
  PhonePrefix,
  SelectOption,
  Tenant,
} from '../types/Types';
import {ApiSettings} from '@planday-corp/core/services';
import {ApiError} from '@planday-corp/core/services/Api';
import Notifications from '@planday-corp/core/services/Notifications';
import {Action} from '@planday-corp/react-app';
import {Label} from '@planday-corp/ui/types';
import {isEmpty} from 'lodash';
import {Store} from 'redux';
import {SagaIterator} from 'redux-saga';
import {all, call, put, take} from 'redux-saga/effects';

export type AppStore = Store<{xeroSignup: XeroSignupState<string>}>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ActionPayload<A> = A extends Action<any, infer P> ? {payload: P} : never;
export type Payload<A> = ActionPayload<Extract<XeroSignupAction, {type: A}>>;

const AppContext = {};

const getErrorToast = (
  message: Label = {
    term: 'SignUp.General_Error',
    fallback: 'Something went wrong, please try again.',
  }
) =>
  ({
    type: 'Toast',
    severity: 'Error',
    message,
  }) as const;

export interface Api {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get: (url: string, opts?: object) => Promise<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  put: (url: string, opts?: object) => Promise<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post: (url: string, opts?: object) => Promise<any>;
  settings: ApiSettings;
}

export interface AppContext {
  signupApi: Api;
  notifications: Notifications;
}

export function* init(context: AppContext): SagaIterator {
  const {signupApi, notifications} = context;

  while (true) {
    const action: ActionTypes = 'xeroSignup/INIT';
    const {
      payload: {signupRequestId, source, signupOrigin, error},
    }: Payload<typeof action> = yield take(action);

    if (source === 'Xero') {
      if (error) {
        yield put(
          actions.receive({
            view: 'errorView',
            signupUrl: `${signupApi.settings.serverUrl}/xeroSignup?source=${signupOrigin}`,
          })
        );
      } else {
        try {
          const tenants: Tenant[] = yield call(
            signupApi.get,
            'XeroSignup/tenants',
            {
              headers: {
                'X-SignupToken': signupRequestId,
              },
            }
          );
          if (
            tenants.length > 1 ||
            (tenants.length === 1 && tenants[0].portalUrl)
          ) {
            yield put(
              actions.receive({
                tenants,
                view: 'organisationSelectionView',
                isLoading: false,
                signupUrl: `${signupApi.settings.serverUrl}/xeroSignup?source=${signupOrigin}`,
              })
            );
          } else {
            yield put(
              actions.receive({
                tenantId: tenants[0].id,
              })
            );
            yield put(actions.fetchFormData());
          }
        } catch {
          yield put(
            actions.receive({
              view: 'errorView',
            })
          );

          yield call([notifications, notifications.add], getErrorToast());
        }
      }
    } else {
      yield put(actions.receive({view: 'emptyView'}));
    }
  }
}

export function* fetchFormData(
  store: AppStore,
  context: AppContext
): SagaIterator {
  const {signupApi} = context;

  while (true) {
    const action: ActionTypes = 'xeroSignup/FETCH_FORM_DATA';
    yield take(action);

    const {signupRequestId, tenantId, source} = store.getState().xeroSignup;

    yield put(
      actions.receive({
        isLoading: true,
        view: 'signupFormView',
      })
    );

    try {
      const [
        employeeOptions,
        industryOptions,
        phonePrefixOptions,
        countryOptions,
        formData,
      ]: [
        SelectOption<string>[],
        SelectOption<string>[],
        PhonePrefix[],
        Country[],
        KeyValue,
      ] = yield all([
        yield call(signupApi.get, '/Common/employees'),
        yield call(signupApi.get, '/Common/industries'),
        yield call(signupApi.get, '/Common/phonePrefixes'),
        yield call(signupApi.get, '/Common/countries', {
          headers: {'Accept-Language': 'en-GB'},
        }),
        source === 'Xero'
          ? yield call(signupApi.get, `/XeroSignup/signupDetails/${tenantId}`, {
              headers: {
                'X-SignupToken': signupRequestId,
              },
            })
          : yield call(signupApi.get, 'XeroSignup/signupDetails', {
              headers: {
                'X-SignupToken': signupRequestId,
              },
            }),
      ]);

      const validationRules = store.getState().xeroSignup.validationRules;

      setValidationRules(formData, validationRules);

      yield put(
        actions.receive({
          employeeOptions,
          industryOptions,
          phonePrefixOptions: formatPhonePrefixes(phonePrefixOptions),
          countryOptions: formatCountries(countryOptions),
          formData,
        })
      );

      yield put(
        actions.receive({
          isLoading: false,
        })
      );
    } catch {
      yield put(actions.receive({view: 'emptyView', isLoading: false}));
    }
  }
}

export function* persist(store: AppStore, context: AppContext): SagaIterator {
  const {signupApi, notifications} = context;

  while (true) {
    const action: ActionTypes = 'xeroSignup/SAVE';
    yield take(action);
    yield call([notifications, notifications.removeAll]);
    const signUpState = store.getState().xeroSignup;
    const {errors, formData, hasAcceptedTerms} = signUpState;

    if (isEmpty(errors.fields) && hasAcceptedTerms) {
      try {
        const payload = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          companyName: formData.companyName,
          countryCode:
            formData.countryCode !== undefined ? formData.countryCode : '',
          timezone:
            formData.countryCode === 'AU' ? formData.timeZone : undefined,
          source: formData.requestOrigin,
          phoneNumber: formData.phone,
          phoneCountryCodePrefix:
            formData.phoneCountryCodePrefix !== undefined
              ? formData.phoneCountryCodePrefix
              : '',
          industry: formData.industry !== undefined ? formData.industry : '',
          employees:
            formData.noOfEmployeesRange !== undefined
              ? formData.noOfEmployeesRange
              : '',
        };

        yield put(
          actions.receive({
            view: 'loadingView',
            isPortalCreationInProgress: true,
          })
        );

        const response = yield call(signupApi.post, 'Signup', {
          body: payload,
          headers: {
            'X-SignupToken': signUpState.signupRequestId,
          },
        });

        const outputModel = response?.outputModel;

        if (outputModel?.status === 'SUCCESSFUL' && outputModel?.autoLoginUrl) {
          yield put(
            actions.receive({
              portalUrl: outputModel.autoLoginUrl,
              isPortalCreationInProgress: false,
            })
          );
        } else {
          yield put(
            actions.receive({
              view: 'signupFormView',
              isPortalCreationInProgress: false,
            })
          );
          yield call(
            [notifications, notifications.add],
            getErrorToast({
              term: 'CommonTerms.Libs_HelperUI_DefaultErrorTitle',
              fallback: 'An error occurred',
            })
          );
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        yield put(actions.receive({view: 'signupFormView'}));
        if (error instanceof ApiError) {
          const validationErrors = error.getValidationErrors();

          const {fields} = error.getValidationErrorsAsObject();

          if (error.isClientError && validationErrors.length) {
            yield put(
              actions.receive({
                apiErrors: {fields},
              })
            );
            yield call(
              [notifications, notifications.add],
              getErrorToast({
                term: 'SignUp.GeneralValidation_ApiError',
                fallback:
                  'An error with the information you entered occurred. Please re-enter your information and try again.',
              })
            );
          } else {
            yield call([notifications, notifications.add], getErrorToast());
          }
        } else {
          yield call([notifications, notifications.add], getErrorToast());
        }
      }
    }
  }
}

export function* proceed(store: AppStore, context: AppContext): SagaIterator {
  const {signupApi, notifications} = context;

  while (true) {
    const action: ActionTypes = 'xeroSignup/PROCEED';
    const {
      payload: {tenantId},
    }: Payload<typeof action> = yield take(action);

    const {signupRequestId} = store.getState().xeroSignup;
    try {
      const response = yield call(
        signupApi.get,
        `/XeroSignup/login/${tenantId}`,
        {
          headers: {
            'X-SignupToken': signupRequestId,
          },
        }
      );

      window.location.replace(response.redirectUrl);
    } catch {
      yield put(
        actions.receive({
          view: 'errorView',
        })
      );
      yield call([notifications, notifications.add], getErrorToast());
    }
  }
}

export function* redirectToPortal(store: AppStore): SagaIterator {
  while (true) {
    const action: ActionTypes = 'xeroSignup/REDIRECT_TO_PORTAL';

    yield take(action);

    const {portalUrl} = store.getState().xeroSignup;
    if (portalUrl) {
      location.href = portalUrl;
    }
  }
}

export function* xeroSignupSaga(store: Store, context: AppContext) {
  yield all([
    init(context),
    fetchFormData(store, context),
    persist(store, context),
    proceed(store, context),
    redirectToPortal(store),
  ]);
}
